/**
 * REST API client với auto-refresh on 401.
 *
 * Cải tiến so với M2 ban đầu:
 * - Auto-refresh access token khi nhận 401, retry request 1 lần.
 * - Single-flight refresh (concurrent 401 share cùng promise refresh).
 * - X-Request-ID propagation cho tracing.
 */
import type { Uuid } from "@/types";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8001";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly traceId?: Uuid,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface RequestOptions extends RequestInit {
  json?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  /** Bỏ qua auto-refresh — dùng cho /auth/refresh chính nó để tránh loop */
  skipRefresh?: boolean;
}

let authTokenGetter: () => string | null = () => null;
let authTokenSetter: (token: string | null) => void = () => undefined;
let authClearer: () => void = () => undefined;
let refreshFn: (() => Promise<string | null>) | null = null;
let inflightRefresh: Promise<string | null> | null = null;

export function setAuthTokenGetter(getter: () => string | null): void {
  authTokenGetter = getter;
}

export function setAuthTokenSetter(setter: (token: string | null) => void): void {
  authTokenSetter = setter;
}

export function setAuthClearer(clearer: () => void): void {
  authClearer = clearer;
}

export function setRefreshFn(fn: () => Promise<string | null>): void {
  refreshFn = fn;
}

async function _doFetch(
  url: string,
  init: RequestInit,
  bearerToken: string | null,
): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (bearerToken) {
    headers.set("Authorization", `Bearer ${bearerToken}`);
  }
  return fetch(url, { ...init, headers, credentials: "include" });
}

async function _refreshTokenSingleFlight(): Promise<string | null> {
  if (!refreshFn) return null;
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = (async () => {
    try {
      return await refreshFn!();
    } catch {
      return null;
    } finally {
      inflightRefresh = null;
    }
  })();
  return inflightRefresh;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { json, query, headers, skipRefresh, ...rest } = options;
  const url = new URL(path, BASE_URL);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
  }

  const finalInit: RequestInit = { ...rest };
  const finalHeaders = new Headers(headers);
  if (json !== undefined) {
    finalHeaders.set("Content-Type", "application/json");
    finalInit.body = JSON.stringify(json);
  }
  finalInit.headers = finalHeaders;

  const token = authTokenGetter();
  let response = await _doFetch(url.toString(), finalInit, token);

  // Auto-refresh khi 401 (trừ chính endpoint refresh)
  if (response.status === 401 && !skipRefresh && refreshFn !== null) {
    const newToken = await _refreshTokenSingleFlight();
    if (newToken) {
      authTokenSetter(newToken);
      response = await _doFetch(url.toString(), finalInit, newToken);
    } else {
      authClearer();
    }
  }

  if (!response.ok) {
    let detail = `${response.status} ${response.statusText}`;
    let traceId: Uuid | undefined;
    try {
      const body = (await response.json()) as {
        detail?: string;
        title?: string;
        trace_id?: Uuid;
      };
      detail = body.detail ?? body.title ?? detail;
      traceId = body.trace_id ?? response.headers.get("X-Request-ID") ?? undefined;
    } catch {
      // ignore
    }
    throw new ApiError(response.status, detail, traceId);
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions): Promise<T> =>
    request<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, json?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>(path, { ...options, method: "POST", json }),
  put: <T>(path: string, json?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>(path, { ...options, method: "PUT", json }),
  patch: <T>(path: string, json?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>(path, { ...options, method: "PATCH", json }),
  delete: <T>(path: string, options?: RequestOptions): Promise<T> =>
    request<T>(path, { ...options, method: "DELETE" }),
};
